import { getAuth } from "firebase/auth";
import {
  collection,
  count,
  doc,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  sum,
  where,
} from "firebase/firestore";

import store from "../../redux/store";
import { setUserToken } from "../../redux/slices/userSlice";
import { firestore, server } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";
import { deriveBudget, normalizeCode } from "../../components/agreements/agreementLogic";

// Los convenios de la Dirección de Extensión viven en su propia colección de
// primer nivel, keyed por el CÓDIGO institucional normalizado — la misma
// decisión que en applicants (keyed por nuip): el código es único por
// definición, así que sirve de llave y ahorra una transacción de unicidad.
// Las lecturas van directo a Firestore; las mutaciones pasan por la función
// handleAgreement, que revalida permisos y deja bitácora.
const COLLECTION = "agreements";

// URL de la Cloud Function del entorno actual. `server` es
// `{region}-{projectId}`, así que cada entorno resuelve a las funciones de SU
// propio proyecto sin URLs hardcodeadas. Igual que applicantFunctionUrl.
export const agreementFunctionUrl = (fn) =>
  `https://${server}.cloudfunctions.net/${fn}`;

// Los Timestamp de Firestore no son serializables para Redux.
const toIso = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (v.toDate) return v.toDate().toISOString();
  if (v instanceof Date) return v.toISOString();
  return null;
};

const mapAgreement = (snap) => {
  const data = snap.data();
  return {
    ...data,
    code: data.code || snap.id,
    // startDate / endDate ya son cadenas ISO date-only: no se tocan.
    createdAt: toIso(data.createdAt),
    updatedAt: toIso(data.updatedAt),
    deletedAt: toIso(data.deletedAt),
  };
};

// Llamada con Bearer token a handleAgreement. Igual que ContractService y
// ApplicantService: ante un 401 se refresca el token cacheado en Redux.
const handleAgreement = async (token, body) => {
  const response = await fetch(agreementFunctionUrl("handleAgreement"), {
    method: "post",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  if (response.status === 401) {
    const auth = getAuth();
    const refreshed = await auth.currentUser.getIdToken();
    store.dispatch(setUserToken(refreshed));
  }

  return response;
};

// Normaliza la respuesta de una mutación a una forma única para los formularios:
// { ok, data, error, errors } — `errors` mapea campo → código de validación, que
// AgreementForm traduce con AGREEMENT_ERROR_LABELS.
const asResult = async (response) => {
  const json = await response.json().catch(() => ({}));
  if (json && json.result) return { ok: true, data: json.body || null, error: null, errors: {} };
  return {
    ok: false,
    data: null,
    error: (json && json.error) || "UNKNOWN",
    errors: (json && json.body && json.body.errors) || {},
  };
};

// --- Lecturas (directo desde Firestore) ------------------------------------

// Página con cursor para el listado. Se pide un elemento de más para saber si
// hay siguiente página sin una segunda consulta. `deletedAt == null` excluye los
// convenios borrados lógicamente.
export const fetchAgreementPage = async ({
  status,
  type,
  expiry,
  // Misma ventana que usa el tablero (la fija la configuración del módulo): si
  // la tarjeta cuenta 45 días, la tabla que abre esa tarjeta tiene que traer los
  // mismos convenios.
  soonDays = 30,
  cursor,
  pageSize = 25,
} = {}) => {
  try {
    const constraints = [where("deletedAt", "==", null)];
    if (status) constraints.push(where("status", "==", status));
    if (type) constraints.push(where("type", "==", type));

    // `expiry` acota por vigencia: "soon" (vence dentro de la ventana) o
    // "expired" (ya venció). Firestore exige que el primer orderBy sea el campo
    // del rango, así que con este filtro la tabla se ordena por vencimiento —que
    // además es el orden que se quiere cuando se está mirando qué se vence— y
    // por fecha de registro cuando no lo hay.
    const today = isoToday();
    if (expiry === "soon") {
      constraints.push(where("endDate", ">=", today), where("endDate", "<=", isoToday(soonDays)));
      constraints.push(orderBy("endDate", "asc"));
    } else if (expiry === "expired") {
      constraints.push(where("endDate", "<", today));
      constraints.push(orderBy("endDate", "desc"));
    } else {
      constraints.push(orderBy("createdAt", "desc"));
    }

    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize + 1));
    const snap = await getDocs(query(collection(firestore, COLLECTION), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    return {
      items: pageDocs.map(mapAgreement),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchAgreementPage error:", e);
    // `error` viaja al listado para que un fallo (índice construyéndose,
    // permisos) no se muestre como "sin resultados".
    return { items: [], lastDoc: null, hasMore: false, error: e?.code || "unknown" };
  }
};

// --- Tablero -----------------------------------------------------------------

// Estructura de la cartera, de UNA lectura: el documento de contadores que
// handleAgreement mantiene en cada escritura (ver gfunctions/extension/_shared/
// agreementStats.js). Cuesta lo mismo con 3 convenios que con 30.000.
//
// Existe porque Firestore no tiene GROUP BY: "por entidad" y "por año de
// vencimiento" no los sirve ningún índice. Si el documento aún no existe —una
// cartera anterior a los contadores—, la acción RECOMPUTE_STATS lo siembra.
export const fetchAgreementStats = async () => {
  try {
    const snap = await getDoc(doc(firestore, "agreementStats", "current"));
    if (!snap.exists()) return { missing: true, error: null };
    const data = snap.data();
    const totalValue = data.totalValue || 0;
    const committedValue = data.committedValue || 0;
    return {
      total: data.total || 0,
      totalValue,
      committedValue,
      ...deriveBudget({ totalValue, committedValue }),
      byStatus: data.byStatus || {},
      byType: data.byType || {},
      byEntity: data.byEntity || {},
      // NIT → razón social, para rotular el gráfico por entidad: el contador va
      // por NIT para que renombrar una entidad no parta su cubo en dos.
      entityNames: data.entityNames || {},
      byEndYear: data.byEndYear || {},
      missing: false,
      error: null,
    };
  } catch (e) {
    console.error("fetchAgreementStats error:", e);
    return { error: e?.code || "unknown" };
  }
};

// Hoy en formato "YYYY-MM-DD", que es como se guardan las fechas del convenio.
const isoToday = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

// Vigentes / vencidos / próximos a vencer. Estos NO salen del contador: dependen
// de la fecha de hoy, no de la última escritura —un convenio se vence mañana sin
// que nadie toque nada—, así que se preguntan en vivo. Son consultas de rango
// sobre endDate, que es justo lo que un índice sabe responder, y de agregación,
// así que no descargan documentos.
export const fetchExpiryCounts = async ({ soonDays = 30 } = {}) => {
  const today = isoToday();
  const soon = isoToday(soonDays);
  const base = (...extra) =>
    query(collection(firestore, COLLECTION), where("deletedAt", "==", null), ...extra);

  try {
    const [active, expired, soonAgg] = await Promise.all([
      getCountFromServer(base(where("endDate", ">=", today))),
      getCountFromServer(base(where("endDate", "<", today))),
      // El monto de lo que está por vencerse es la cifra que acompaña al aviso,
      // así que se pide junto al conteo en la misma consulta.
      getAggregateFromServer(
        base(where("endDate", ">=", today), where("endDate", "<=", soon)),
        { total: count(), totalValue: sum("totalValue") }
      ),
    ]);

    return {
      active: active.data().count || 0,
      expired: expired.data().count || 0,
      soon: soonAgg.data().total || 0,
      soonValue: soonAgg.data().totalValue || 0,
      soonDays,
      today,
      soonUntil: soon,
      error: null,
    };
  } catch (e) {
    console.error("fetchExpiryCounts error:", e);
    return { error: e?.code || "unknown" };
  }
};

export const getAgreement = async (code) => {
  try {
    const snap = await getDoc(doc(firestore, COLLECTION, normalizeCode(code)));
    if (!snap.exists()) return null;
    const agreement = mapAgreement(snap);
    // Un convenio borrado no existe para las vistas.
    return agreement.deletedAt ? null : agreement;
  } catch (e) {
    console.error("getAgreement error:", e);
    return null;
  }
};

// --- Mutaciones (vía handleAgreement) --------------------------------------

export const createAgreement = async (agreement, user) => {
  try {
    const response = await handleAgreement(user.token, { action: "CREATE_AGREEMENT", agreement });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// `agreement` es un patch: sólo los campos que cambian. El servidor lo fusiona
// con el documento guardado antes de validar, ignora los campos inmutables
// (código, valores originales) y recalcula el presupuesto.
export const updateAgreement = async (code, agreement, user) => {
  try {
    const response = await handleAgreement(user.token, {
      action: "UPDATE_AGREEMENT",
      code: normalizeCode(code),
      agreement,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// Borrado lógico: el convenio sale del listado pero el documento se conserva,
// porque es la referencia de contratos ya firmados.
export const deleteAgreement = async (code, user) => {
  try {
    const response = await handleAgreement(user.token, {
      action: "DELETE_AGREEMENT",
      code: normalizeCode(code),
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// Bitácora del convenio (colección agreementAudit), más reciente primero. Sólo
// ADMIN. Devuelve [] ante cualquier fallo para que el drawer muestre "sin
// eventos" en vez de romperse.
export const getAgreementLogs = async (code, user) => {
  try {
    const response = await handleAgreement(user.token, {
      action: "GET_AGREEMENT_LOGS",
      code: normalizeCode(code),
    });
    const json = await response.json();
    return json.result ? (json.body || []) : [];
  } catch (error) {
    debugLog(error);
    return [];
  }
};
